<template>
    <div :class="`dk-score dk-score--${variant}`">
        <template v-if="isVariantBadge">
            <div
                class="badge-stamp badge-stamp--inline badge-stamp--rounded ts-weight--700 p-top--xxs p-bottom--xxs p-left--xs p-right--xs bg--primary-dark color--white"
            >
                <div class="d--flex items-baseline m-right--xxs">
                    <small class="ts--small">{{ scoreLabel }}</small>
                    <span class="opacity-50% ts--micro">&#47;{{ maxScoreLabel }}</span>
                </div>
                <small class="ts--small ts--capitalize">{{ scoreText }}</small>
            </div>
        </template>
        <template v-else>
            <svg :viewBox="`0 0 ${viewBox} ${viewBox}`" :width="size" :heigth="size" class="dk-score__gauge">
                <defs>
                    <linearGradient id="gradient-primary" gradientUnits="userSpaceOnUse">
                        <stop stop-color="#339bce" />
                        <stop offset="1" stop-color="#00568c" />
                    </linearGradient>
                </defs>
                <title>{{ title }}</title>
                <path
                    :d="dDial"
                    :stroke-width="strokeWidth"
                    :stroke-linecap="strokeLinecap"
                    class="dk-score__dial"
                    fill="none"
                    stroke="#ccd2d9"
                />
                <g v-if="showValue" class="dk-score__value-text">
                    <text
                        :x="viewBox / 2"
                        :y="viewBox / 2"
                        text-anchor="middle"
                        alignment-baseline="middle"
                        dy="0.25em"
                    >
                        <tspan :font-size="showMaxScore ? '12' : '16'" font-weight="800" fill="#434c56">
                            {{ scoreLabel }}
                        </tspan>
                        <tspan
                            v-if="showMaxScore"
                            font-size="8"
                            font-weight="700"
                            fill="#63707f"
                            dx="-2"
                        >
                            &#47;{{ maxScoreLabel }}
                        </tspan>
                    </text>
                </g>
                <path
                    v-if="value"
                    :d="d"
                    :stroke-width="strokeWidth"
                    :stroke-linecap="strokeLinecap"
                    class="dk-score__value"
                    fill="none"
                    stroke="url(#gradient-primary)"
                />
            </svg>
            <!-- @slot Content below the gauge. Available only when variant it's `gauge` -->
            <slot v-bind="{ scoreText, score }"></slot>
        </template>
    </div>
</template>

<script>
export const DkScoreVariantsEnum = Object.freeze({ GAUGE: 'gauge', BADGE: 'badge' });

/**
 * DkScore component.
 *
 * Inspired by [svg-gauge](https://www.npmjs.com/package/svg-gauge) plugin.
 *
 * Use a [CSS 3 -> SVG image converter](http://www.kmhcreative.com/downloads/CSS2SVG.htm)
 * in order to obtain the `lienearGradient` tag.
 *
 * @version 4.0.0
 * @example ./examples/DkScore.md
 */
export default {
    name: 'DkScore',
    props: {
        /**
         * Score value
         */
        score: {
            type: [Number, String],
            default: 0,
        },
        /**
         * The angle to start the dial. MUST be greater than dialEndAngle
         */
        startAngle: {
            type: [Number, String],
            default: 135,
        },
        /**
         * The angle to end the dial
         */
        endAngle: {
            type: [Number, String],
            default: 45,
        },
        /**
         * The gauge's radius
         */
        radius: {
            type: [Number, String],
            default: 18,
        },
        /**
         * Score max value
         */
        maxScore: {
            type: [Number, String],
            default: 5,
        },
        /**
         * Whether to show the score value at the center of the gauge
         */
        showValue: {
            type: Boolean,
            default: true,
        },
        /**
         * Whether to show the max score value at the center of the gauge
         */
        showMaxScore: {
            type: Boolean,
            default: true,
        },
        /**
         * Sets the stroke width
         */
        strokeWidth: {
            type: [Number, String],
            default: 3,
        },
        /**
         * The shape to be used at the end of open subpaths. Available values: `butt`, `round`, `square`
         */
        strokeLinecap: {
            type: String,
            default: 'round',
        },
        /**
         * SVG viewBox width and height
         */
        viewBox: {
            type: [Number, String],
            default: 45,
        },
        /**
         * SVG title
         */
        title: {
            type: String,
            default: 'DriveK Score',
        },
        /**
         * SVG size width and height
         */
        size: {
            type: [Number, String],
            default: 75,
        },
        /**
         * UI variants. Available values: `gauge`, `badge`. Default: `gauge`
         */
        variant: {
            type: String,
            default: 'gauge',
            validator: (value) => Object.values(DkScoreVariantsEnum).includes(value),
        },
    },
    data() {
        return {
            min: 0,
            max: 100,
        };
    },
    computed: {
        value() {
            const value = Number((100 / this.maxScore) * this.score);

            if (value > this.max) {
                return this.max;
            }

            if (value < this.min) {
                return this.min;
            }

            return value;
        },
        dDial() {
            return this.pathString(this.radius, this.startAngle, this.endAngle);
        },
        d() {
            const val = this.getValueInPercentage(this.value, this.min, this.max);
            const angle = this.getAngle(val, 360 - Math.abs(this.startAngle - this.endAngle));
            const flag = angle <= 180 ? 0 : 1;

            return this.pathString(this.radius, this.startAngle, angle + this.startAngle, flag);
        },
        scoreText() {
            if (this.value > 86) {
                return this.$t('dk-score.excellent');
            } else if (this.value > 76) {
                return this.$t('dk-score.very-good');
            } else if (this.value > 60) {
                return this.$t('dk-score.good');
            }

            return this.$t('dk-score.average');
        },
        scoreLabel() {
            return this.score.toLocaleString(document.documentElement.lang, { minimumFractionDigits: 1 });
        },
        maxScoreLabel() {
            return this.maxScore.toLocaleString(document.documentElement.lang);
        },
        isVariantBadge() {
            return this.variant === DkScoreVariantsEnum.BADGE;
        },
    },
    methods: {
        getValueInPercentage(value, min, max) {
            const newMax = max - min;
            const newVal = value - min;

            return (100 * newVal) / newMax;
        },
        /**
         * Translates percentage value to angle.
         * e.g. If gauge span angle is 180deg, then 50% will be 90deg
         *
         * @returns {number}
         */
        getAngle(percentage, gaugeSpanAngle) {
            return (percentage * gaugeSpanAngle) / 100;
        },
        pathString(radius, startAngle, endAngle, largeArc) {
            const coords = this.getDialCoords(radius, startAngle, endAngle);
            const start = coords.start;
            const end = coords.end;
            const largeArcFlag = typeof largeArc === 'undefined' ? 1 : largeArc;

            return ['M', start.x, start.y, 'A', radius, radius, 0, largeArcFlag, 1, end.x, end.y].join(' ');
        },
        getDialCoords(radius, startAngle, endAngle) {
            const cx = this.viewBox / 2;
            const cy = this.viewBox / 2;

            return {
                end: this.getCartesian(cx, cy, radius, endAngle),
                start: this.getCartesian(cx, cy, radius, startAngle),
            };
        },
        /**
         * Gets cartesian co-ordinates for a specified radius and angle (in degrees)
         *
         * @param {number} cx The center x co-oriinate
         * @param {number} cy The center y co-ordinate
         * @param {number} radius The radius of the circle
         * @param {number} angle The angle in degrees
         * @return {object} An object with x,y co-ordinates
         */
        getCartesian(cx, cy, radius, angle) {
            const rad = (angle * Math.PI) / 180;

            return {
                x: Math.round((cx + radius * Math.cos(rad)) * 1000) / 1000,
                y: Math.round((cy + radius * Math.sin(rad)) * 1000) / 1000,
            };
        },
    },
};
</script>
